import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useAIGenerationEnabled } from '../hooks/useAIGenerationEnabled';
import { useCreditsCheck } from '../hooks/useCreditsCheck';
import { supabase } from '../lib/supabase';
import { ListPageSkeleton } from '../components/skeletons/ListPageSkeleton';
import { MaintenanceBanner } from '../components/MaintenanceBanner';
import { LowCreditsBanner } from '../components/LowCreditsBanner';
import { Sidebar } from '../components/Sidebar';
import { MobileNav } from '../components/MobileNav';
import { Calendar, Eye, CreditCard as Edit2, ExternalLink, EyeOff, Mail, Trash2, Sparkles, Plus, Menu, Settings, LogOut, AlertTriangle, Globe } from 'lucide-react';
import { getBlogUrlConfigForUser, generateBlogUrl, generateWidgetBlogUrl, generateBlogListUrl } from '../lib/blogUrlHelper';
import { PageHeader } from '../components/design-system/PageHeader';
import { SearchBar } from '../components/design-system/SearchBar';
import { ConceptList } from '../components/ConceptList';
import { returnIdeaToInboxBySession } from '../lib/capturedIdeas';
import { resumeRouteForSession, resumeRouteForState } from '../lib/resumeRoute';
import { CONCEPT_STATES, conceptTitle, sortConceptsByEdited, type ConceptSession } from '../lib/concepts';
import { wizardUrl } from '../lib/wizardRoutes';

interface PublishedBlog {
  id: string;
  slug: string;
  title: string;
  intro: string;
  featured_image_url: string | null;
  published_at: string;
  updated_at: string;
  view_count: number;
  share_count: number;
  status: string;
  is_active: boolean;
  wp_post_url: string | null;
  wp_status: string | null;
}

export function MyBlogs() {
  const { user: sessionUser } = useSession();
  const { user: authUser } = useAuth();
  const user = authUser || sessionUser;
  const navigate = useNavigate();
  const { isEnabled: aiGenerationEnabled } = useAIGenerationEnabled();
  const creditsInfo = useCreditsCheck(user?.id);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState<PublishedBlog[]>([]);
  const [usernameSlug, setUsernameSlug] = useState<string | null>(null);
  const [widgetEnabled, setWidgetEnabled] = useState(false);
  const [blogUrlConfig, setBlogUrlConfig] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState<PublishedBlog | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllBlogs, setShowAllBlogs] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'concepten' | 'gepubliceerd'>('gepubliceerd');
  const [concepts, setConcepts] = useState<ConceptSession[]>([]);
  const [conceptToDelete, setConceptToDelete] = useState<ConceptSession | null>(null);
  const [deletingConcept, setDeletingConcept] = useState(false);
  const tabInitialized = useRef(false);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadBlogs();
    } else {
      navigate('/');
    }
  }, [user, navigate]);


  const loadBlogs = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data: userData } = await supabase
        .from('users')
        .select('username_slug, widget_enabled')
        .eq('id', user.id)
        .single();

      if (userData) {
        setUsernameSlug(userData.username_slug);
        setWidgetEnabled(userData.widget_enabled || false);
      }

      const urlConfig = await getBlogUrlConfigForUser(user.id, supabase);
      setBlogUrlConfig(urlConfig);

      const { data, error } = await supabase
        .from('published_blogs')
        .select('id, slug, title, intro, featured_image_url, published_at, updated_at, view_count, share_count, status, is_active, wp_post_url, wp_status')
        .eq('user_id', user.id)
        .is('deleted_at', null)
        .order('published_at', { ascending: false });

      if (error) {
        console.error('Error loading blogs:', error);
      } else {
        setBlogs(data || []);
      }

      const { data: conceptData, error: conceptError } = await supabase
        .from('sessions')
        .select('id, state, selected_title, selected_intro, selected_image_url, article_last_edited, created_at, blog_ideas!sessions_approved_idea_id_fkey(idea_title)')
        .eq('user_id', user.id)
        .in('state', CONCEPT_STATES);

      const conceptRows = conceptError
        ? []
        : sortConceptsByEdited((conceptData ?? []) as unknown as ConceptSession[]);
      if (conceptError) console.error('Error loading concepts:', conceptError);
      setConcepts(conceptRows);

      if (!tabInitialized.current) {
        setActiveTab(conceptRows.length > 0 ? 'concepten' : 'gepubliceerd');
        tabInitialized.current = true;
      }
    } catch (err) {
      console.error('Error loading blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleBlogActive = async (blog: PublishedBlog) => {
    try {
      const { error } = await supabase
        .from('published_blogs')
        .update({ is_active: !blog.is_active })
        .eq('id', blog.id);

      if (error) {
        console.error('Error toggling blog status:', error);
        alert('Er ging iets mis bij het wijzigen van de blog status');
      } else {
        loadBlogs();
      }
    } catch (err) {
      console.error('Error toggling blog status:', err);
      alert('Er ging iets mis bij het wijzigen van de blog status');
    }
  };

  const deleteBlog = async () => {
    if (!blogToDelete) return;

    try {
      setDeleting(true);

      // Soft delete: set deleted_at timestamp
      const { error } = await supabase
        .from('published_blogs')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', blogToDelete.id);

      if (error) {
        console.error('Error deleting blog:', error);
        alert('Er ging iets mis bij het verwijderen van de blog');
        return;
      }

      // Close modal and reload
      setShowDeleteModal(false);
      setBlogToDelete(null);
      loadBlogs();
    } catch (err) {
      console.error('Error deleting blog:', err);
      alert('Er ging iets mis bij het verwijderen van de blog');
    } finally {
      setDeleting(false);
    }
  };

  const openBlog = (blog: PublishedBlog) => {
    if (!blogUrlConfig) return;

    const url = widgetEnabled
      ? generateWidgetBlogUrl(blog.slug, blogUrlConfig, blog.wp_post_url)
      : generateBlogUrl(blog.slug, blogUrlConfig, blog.wp_post_url);

    window.open(url, '_blank');
  };

  const continueConcept = async (concept: ConceptSession) => {
    const { data } = await supabase
      .from('sessions')
      .select('id, state, article_content, selected_title, selected_intro, selected_image_url, titles, intros, images')
      .eq('id', concept.id)
      .maybeSingle();
    const route = data ? resumeRouteForSession(data) : resumeRouteForState(concept.state);
    navigate(wizardUrl(route, concept.id));
  };

  const requestDeleteConcept = (concept: ConceptSession) => {
    setConceptToDelete(concept);
  };

  const confirmDeleteConcept = async () => {
    if (!conceptToDelete) return;
    const concept = conceptToDelete;
    setDeletingConcept(true);
    try {
      await supabase.from('sessions').update({ state: 'abandoned' }).eq('id', concept.id);
      await returnIdeaToInboxBySession(concept.id);
      setConcepts((prev) => prev.filter((c) => c.id !== concept.id));
      setConceptToDelete(null);
      showToast('Concept verwijderd', 'success');
    } catch (err) {
      console.error('Error deleting concept:', err);
      showToast('Verwijderen mislukt', 'error', 'Probeer het opnieuw.');
    } finally {
      setDeletingConcept(false);
    }
  };

  // Filter en search logic
  const filteredBlogs = blogs.filter(blog => {
    if (searchQuery) {
      return blog.title.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  const displayedBlogs = showAllBlogs ? filteredBlogs : filteredBlogs.slice(0, 10);

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <Sidebar onSignOut={handleSignOut} />
      <MobileNav
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onSignOut={handleSignOut}
      />
      <div className="lg:pl-64">
        <MaintenanceBanner />
        <LowCreditsBanner />

        <header className="bg-white border-b border-gray-200 sticky top-0 z-40 lg:hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
                <img
                  src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                  alt="Dibbi Logo"
                  className="h-8 lg:hidden"
                />
              </div>
              <div className="flex items-center gap-3 lg:hidden">
                <button
                  onClick={() => navigate('/dashboard/settings')}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Instellingen"
                >
                  <Settings className="w-6 h-6" />
                </button>
                <button
                  onClick={handleSignOut}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Uitloggen"
                >
                  <LogOut className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
        {loading ? (
          <ListPageSkeleton rows={5} withTabs={false} />
        ) : !usernameSlug ? (
          <div className="card text-center max-w-xl mx-auto">
            <h2 className="text-2xl font-bold text-[#231F20] mb-4">
              Geen username ingesteld
            </h2>
            <p className="text-gray-600 mb-6">
              Je moet eerst een username instellen voordat je blogs kunt publiceren.
              Een username bepaalt de URL waar je blogs te vinden zijn
            </p>
            <button
              onClick={() => navigate('/dashboard/settings')}
              className="btn-primary"
            >
              Ga naar instellingen om username in te stellen
            </button>
          </div>
        ) : (
        <div className="space-y-6">
          <PageHeader
            title="Blogs"
            subtitle="Beheer je blogs en concepten"
            actions={
              <>
                {blogUrlConfig && blogs.length > 0 && (
                  <a
                    href={generateBlogListUrl(blogUrlConfig)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary inline-flex items-center gap-2"
                  >
                    <ExternalLink className="w-5 h-5" />
                    <span className="hidden sm:inline">Blog Overzicht</span>
                  </a>
                )}
                <button
                  onClick={() => navigate('/dashboard/new-blog')}
                  disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                  title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits, minimum is ${creditsInfo.negativeLimit}.` : ''}
                  className={`btn-primary inline-flex items-center gap-2 ${(!creditsInfo.hasCredits || !aiGenerationEnabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Plus className="w-5 h-5" />
                  <span className="hidden sm:inline">Nieuwe Blog</span>
                </button>
              </>
            }
          />

          <div className="border-b border-gray-200">
            <nav className="flex gap-6">
              <button
                onClick={() => setActiveTab('concepten')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'concepten'
                    ? 'border-[#FF6B35] text-[#231F20]'
                    : 'border-transparent text-gray-500 hover:text-[#231F20]'
                }`}
              >
                Concepten ({concepts.length})
              </button>
              <button
                onClick={() => setActiveTab('gepubliceerd')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'gepubliceerd'
                    ? 'border-[#FF6B35] text-[#231F20]'
                    : 'border-transparent text-gray-500 hover:text-[#231F20]'
                }`}
              >
                Gepubliceerd ({blogs.length})
              </button>
            </nav>
          </div>

          {activeTab === 'concepten' && (
            <ConceptList
              concepts={concepts}
              onContinue={continueConcept}
              onDelete={requestDeleteConcept}
            />
          )}

          {activeTab === 'gepubliceerd' && (<>
          {!creditsInfo.hasCredits && (
            <div className="card bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-900 mb-1">
                    Onvoldoende credits om nieuwe blogs te maken
                  </p>
                  <p className="text-sm text-red-800 mb-3">
                    Je hebt {creditsInfo.total} credits. Je kunt geen nieuwe blogs maken tot je credits zijn bijgevuld.
                    {creditsInfo.nextBillingDate && (
                      <span className="block mt-1">
                        Volgende automatische bijvulling: <span className="font-semibold">{new Date(creditsInfo.nextBillingDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      </span>
                    )}
                  </p>
                  <a
                    href="/dashboard/settings"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Credits Aankopen
                  </a>
                </div>
              </div>
            </div>
          )}

          {blogs.length > 3 && (
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Zoek blog..."
            />
          )}

          {!showAllBlogs && filteredBlogs.length > 10 && searchQuery.trim() === '' && (
            <div className="card text-center">
              <button
                onClick={() => setShowAllBlogs(true)}
                className="text-gray-600 hover:text-[#231F20] font-medium"
              >
                Toon alle {filteredBlogs.length} blogs
              </button>
            </div>
          )}

          {showAllBlogs && searchQuery.trim() === '' && (
            <div className="card text-center">
              <button
                onClick={() => setShowAllBlogs(false)}
                className="text-gray-600 hover:text-[#231F20] font-medium"
              >
                Toon alleen eerste 10 blogs
              </button>
            </div>
          )}

        {blogs.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 text-center py-12">
            <p className="text-xl text-gray-600 mb-4">
              Je hebt nog geen blogs gepubliceerd
            </p>
            <p className="text-gray-500 mb-6">
              Voltooi de blog wizard om je eerste blog te publiceren
            </p>
          </div>
        ) : displayedBlogs.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 text-center py-12">
            <p className="text-xl text-gray-600 mb-4">
              Geen blogs gevonden
            </p>
            <p className="text-gray-500">
              Probeer een andere zoekopdracht
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayedBlogs.map((blog) => (
              <div key={blog.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow">
                <div className="flex flex-col md:flex-row gap-4">
                  {blog.featured_image_url && (
                    <img
                      src={blog.featured_image_url}
                      alt={blog.title}
                      className="w-full md:w-40 h-28 object-cover rounded-lg"
                    />
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-[#231F20] line-clamp-1">
                        {blog.title}
                      </h3>
                      {!blog.is_active && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full whitespace-nowrap">
                          <EyeOff className="w-3 h-3" />
                          Niet actief
                        </span>
                      )}
                      {blog.wp_status === 'publish' && blog.wp_post_url && (
                        <a
                          href={blog.wp_post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-800 text-xs rounded-full whitespace-nowrap hover:bg-emerald-200 transition-colors"
                          title="Bekijk dit artikel op je eigen website"
                        >
                          <Globe className="w-3 h-3" />
                          Op je website
                        </a>
                      )}
                      {blog.wp_status === 'draft' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full whitespace-nowrap" title="Staat als concept klaar op je website">
                          <Globe className="w-3 h-3" />
                          Concept op je website
                        </span>
                      )}
                    </div>

                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {blog.intro}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>
                          {new Date(blog.published_at).toLocaleDateString('nl-NL')}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        <span>{blog.view_count} views</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate(`/blog/edit/${blog.id}`)}
                        className="btn-primary inline-flex items-center gap-2 text-sm"
                      >
                        <Edit2 className="w-4 h-4" />
                        Bewerken
                      </button>

                      <button
                        onClick={() => openBlog(blog)}
                        className="btn-secondary inline-flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!blog.is_active}
                      >
                        <ExternalLink className="w-4 h-4" />
                        Bekijk
                      </button>

                      <button
                        onClick={() => navigate(`/campaigns/create/from-blog?blog=${blog.id}`)}
                        disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                        title={
                          !aiGenerationEnabled
                            ? 'AI generatie is tijdelijk uitgeschakeld'
                            : !creditsInfo.hasCredits
                              ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits, minimum is ${creditsInfo.negativeLimit}.`
                              : 'Maak Campaign'
                        }
                        className={`btn-secondary inline-flex items-center gap-2 text-sm ${
                          (!creditsInfo.hasCredits || !aiGenerationEnabled)
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                      >
                        <Mail className="w-4 h-4" />
                        Campaign
                      </button>

                      <button
                        onClick={() => navigate(`/social-media/create/${blog.id}`)}
                        disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                        title={
                          !aiGenerationEnabled
                            ? 'AI generatie is tijdelijk uitgeschakeld'
                            : !creditsInfo.hasCredits
                              ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits, minimum is ${creditsInfo.negativeLimit}.`
                              : 'Maak social media post'
                        }
                        className={`btn-secondary inline-flex items-center gap-2 text-sm ${
                          (!creditsInfo.hasCredits || !aiGenerationEnabled)
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                      >
                        <Sparkles className="w-4 h-4" />
                        Social
                      </button>

                      <button
                        onClick={() => toggleBlogActive(blog)}
                        className="btn-secondary inline-flex items-center gap-2 text-sm hover:bg-gray-100"
                      >
                        <EyeOff className="w-4 h-4" />
                        {blog.is_active ? 'Uit' : 'Aan'}
                      </button>

                      <button
                        onClick={() => {
                          setBlogToDelete(blog);
                          setShowDeleteModal(true);
                        }}
                        className="btn-secondary inline-flex items-center gap-2 text-sm hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                        Verwijder
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
          </>)}
        </div>
        )}
      </main>
      </div>
      {showDeleteModal && blogToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Blog Verwijderen</h3>

            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                Weet je zeker dat je deze blog wilt verwijderen?
              </p>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="font-semibold text-gray-900 mb-1">{blogToDelete.title}</p>
                <p className="text-sm text-gray-600">
                  {new Date(blogToDelete.published_at).toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Let op:</strong> De blog wordt permanent verwijderd uit je lijst.
                  Deze actie kan niet ongedaan gemaakt worden.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setBlogToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={deleting}
              >
                Annuleren
              </button>
              <button
                onClick={deleteBlog}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deleting}
              >
                {deleting ? 'Verwijderen...' : 'Verwijderen'}
              </button>
            </div>
          </div>
        </div>
      )}
      {conceptToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Concept verwijderen</h3>
            <div className="mb-6">
              <p className="text-gray-700 mb-3">Weet je zeker dat je dit concept wilt verwijderen?</p>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="font-semibold text-gray-900">{conceptTitle(conceptToDelete)}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConceptToDelete(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                disabled={deletingConcept}
              >
                Annuleren
              </button>
              <button
                onClick={confirmDeleteConcept}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deletingConcept}
              >
                {deletingConcept ? 'Verwijderen...' : 'Verwijderen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
